import {
  Box,
  Flex,
  Grid,
  HStack,
  Heading,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiActivity,
  FiEdit,
  FiGrid,
  FiList,
  FiPlus,
  FiSearch,
  FiTag,
  FiThermometer,
  FiTrash2,
} from "react-icons/fi"

import { TagTemplatesService } from "@/client"
import type { ApiError, TagTemplateRead, TagTypeEnum } from "@/client"
import ConfirmDialog from "@/components/Common/ConfirmDialog"
import {
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
} from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import { InputGroup } from "@/components/ui/input-group"
import { OrganizationContext } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { getTagTypeMeta } from "@/lib/tagTypeMeta"

export const Route = createFileRoute("/_layout/tag-templates")({
  component: TagTemplatesPage,
})

// Tag-type chrome lives in a shared module so list + form render the
// same icon and colour for every enum value (see
// frontend/src/lib/tagTypeMeta.ts). Adding a new tag type to the
// backend enum only requires updating that file.
const getTagTypeIcon = (type: TagTypeEnum) => getTagTypeMeta(type).icon
const getTagTypeColor = (type: TagTypeEnum) => getTagTypeMeta(type).color

function TagTemplatesPage() {
  const { t } = useTranslation(["tags", "common"])
  const sc = useSemanticColors()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const navigate = useNavigate()
  const { currentOrganization } = useContext(OrganizationContext)

  const [search, setSearch] = useState("")
  const [viewMode, setViewMode] = useState<"grid" | "table">(
    () =>
      (localStorage.getItem("templates-view-mode") as "grid" | "table") ||
      "grid",
  )
  const updateViewMode = (mode: "grid" | "table") => {
    setViewMode(mode)
    localStorage.setItem("templates-view-mode", mode)
  }
  const [templateToDelete, setTemplateToDelete] =
    useState<TagTemplateRead | null>(null)

  const {
    data: templates = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["tag-templates", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) return []
      try {
        return await TagTemplatesService.readOrganizationTemplates({
          organisationId: currentOrganization.id,
          skip: 0,
          limit: 100,
        })
      } catch {
        return []
      }
    },
    enabled: !!currentOrganization,
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) =>
      TagTemplatesService.deleteOrganizationTemplate({
        organisationId: currentOrganization!.id,
        id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tag-templates"] })
      showToast.showSuccessToast(t("tags:templateDeleted"))
      setTemplateToDelete(null)
    },
    onError: (err: ApiError) => {
      const detail =
        (err.body as Record<string, string>)?.detail || t("common:toast.error")
      showToast.showErrorToast(detail)
    },
  })

  const filteredTemplates = templates.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  )

  const handleDeleteTemplate = (template: TagTemplateRead) => {
    setTemplateToDelete(template)
  }

  if (!currentOrganization) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner />
      </Flex>
    )
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Heading size="xl" color={sc.heading} mb={2}>
            {t("tags:tagTemplates")}
          </Heading>
          <Text color={sc.textMuted}>
            {t("tags:subtitle", { orgName: currentOrganization.name })}
          </Text>
        </Box>
        <HStack gap={2}>
          <HStack bg={sc.toggleBg} borderRadius="10px" p="2px">
            <Box
              as="button"
              p={2}
              borderRadius="8px"
              bg={viewMode === "grid" ? sc.toggleActiveBg : "transparent"}
              color={viewMode === "grid" ? sc.heading : sc.textMuted}
              onClick={() => updateViewMode("grid")}
            >
              <FiGrid size={18} />
            </Box>
            <Box
              as="button"
              p={2}
              borderRadius="8px"
              bg={viewMode === "table" ? sc.toggleActiveBg : "transparent"}
              color={viewMode === "table" ? sc.heading : sc.textMuted}
              onClick={() => updateViewMode("table")}
            >
              <FiList size={18} />
            </Box>
          </HStack>
          <VisionButton
            variant="brand"
            onClick={() =>
              navigate({
                to: "/template-form",
                search: { templateId: undefined },
              })
            }
          >
            <FiPlus />
            {t("tags:addTemplate")}
          </VisionButton>
        </HStack>
      </Flex>

      <VisionCard mb={6}>
        <InputGroup startElement={<FiSearch color={sc.iconMuted} />}>
          <VisionInput
            placeholder={t("tags:searchTemplates")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        {search && (
          <Text fontSize="xs" color={sc.textSubtle} mt={1}>
            {t("tags:templateResultCount", {
              filtered: filteredTemplates.length,
              total: templates.length,
            })}
          </Text>
        )}
      </VisionCard>

      {isLoading ? (
        <Flex justify="center" py={10}>
          <Spinner />
        </Flex>
      ) : error ? (
        <Text color="red.400">{t("common:toast.error")}</Text>
      ) : viewMode === "grid" ? (
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          }}
          gap={6}
        >
          {filteredTemplates.map((template) => (
            <VisionCard key={template.id} variant="gradient">
              <Flex direction="column" h="100%">
                <Flex justify="space-between" align="flex-start" mb={4}>
                  <Box flex="1">
                    <Flex align="center" gap={2} mb={2}>
                      <Heading size="md" color={sc.heading}>
                        {template.name}
                      </Heading>
                      <Text fontSize="2xl">
                        {getTagTypeIcon(template.tag_type)}
                      </Text>
                      {template.is_default && (
                        <Badge colorPalette="blue" size="sm" variant="surface">
                          {t("tags:defaultBadge")}
                        </Badge>
                      )}
                      {template.is_protected_datalogger && (
                        <Badge
                          colorPalette="orange"
                          size="sm"
                          variant="surface"
                        >
                          {t("tags:dataloggerBadge")}
                        </Badge>
                      )}
                    </Flex>
                    <VStack align="start" gap={2}>
                      <Badge
                        colorScheme={getTagTypeColor(template.tag_type)}
                        size="sm"
                      >
                        {template.tag_type}
                      </Badge>
                      {template.unit && (
                        <HStack gap={2}>
                          <FiActivity fontSize="14px" color={sc.textMuted} />
                          <Text color={sc.textSecondary} fontSize="sm">
                            {t("tags:table.unit")}: {template.unit}
                          </Text>
                        </HStack>
                      )}
                      {(template.min_threshold !== undefined ||
                        template.max_threshold !== undefined) && (
                        <HStack gap={2}>
                          <FiThermometer fontSize="14px" color={sc.textMuted} />
                          <Text color={sc.textSecondary} fontSize="sm">
                            {t("tags:table.range")}:{" "}
                            {template.min_threshold ?? "-∞"} to{" "}
                            {template.max_threshold ?? "∞"}
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  </Box>
                  <IconBox variant="glass" size="md">
                    <FiTag fontSize="20px" color={sc.iconColor} />
                  </IconBox>
                </Flex>

                <Flex gap={2} mt="auto">
                  <VisionButton
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      navigate({
                        to: "/template-form",
                        search: { templateId: template.id },
                      })
                    }
                    flex="1"
                  >
                    <FiEdit /> {t("common:actions.edit")}
                  </VisionButton>
                  {!template.is_default &&
                    !template.is_protected_datalogger && (
                      <VisionButton
                        variant="ghost"
                        size="sm"
                        colorPalette="red"
                        onClick={() => handleDeleteTemplate(template)}
                      >
                        <FiTrash2 />
                      </VisionButton>
                    )}
                </Flex>
              </Flex>
            </VisionCard>
          ))}

          {filteredTemplates.length === 0 && (
            <VisionCard variant="outline">
              <Flex direction="column" align="center" justify="center" py={8}>
                <IconBox variant="glass" size="lg" mb={4}>
                  <FiTag fontSize="24px" color={sc.iconColor} />
                </IconBox>
                <Heading size="md" color={sc.heading} mb={2}>
                  {t("tags:noTemplates")}
                </Heading>
                <Text color={sc.textMuted} textAlign="center" mb={4}>
                  {t("tags:noTemplatesDesc")}
                </Text>
                <VisionButton
                  variant="brand"
                  onClick={() =>
                    navigate({
                      to: "/template-form",
                      search: { templateId: undefined },
                    })
                  }
                >
                  <FiPlus /> {t("tags:addTemplate")}
                </VisionButton>
              </Flex>
            </VisionCard>
          )}
        </Grid>
      ) : (
        <VisionCard>
          {filteredTemplates.length === 0 ? (
            <Flex direction="column" align="center" py={10} gap={3}>
              <FiTag fontSize="36px" color={sc.iconMuted} />
              <Text color={sc.textMuted} fontSize="sm">
                {search
                  ? t("tags:noMatchingTemplates", { search })
                  : t("tags:noTemplatesDesc")}
              </Text>
            </Flex>
          ) : (
            <Table.Root size={{ base: "sm", md: "md" }}>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader color={sc.tableHeaderColor}>
                    {t("common:table.name")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.tableHeaderColor}>
                    {t("tags:table.type")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.tableHeaderColor}>
                    {t("tags:table.unit")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.tableHeaderColor}>
                    {t("tags:table.min")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.tableHeaderColor}>
                    {t("tags:table.max")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.tableHeaderColor}>
                    {t("tags:form.chipTypeColumn")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.tableHeaderColor}>
                    {t("common:table.actions")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredTemplates.map((template) => (
                  <Table.Row key={template.id}>
                    <Table.Cell color={sc.heading} fontWeight="500">
                      <HStack gap={2}>
                        <Text>{template.name}</Text>
                        {template.is_default && (
                          <Badge
                            colorPalette="blue"
                            size="sm"
                            variant="surface"
                          >
                            {t("tags:defaultBadge")}
                          </Badge>
                        )}
                        {template.is_protected_datalogger && (
                          <Badge
                            colorPalette="orange"
                            size="sm"
                            variant="surface"
                          >
                            {t("tags:dataloggerBadge")}
                          </Badge>
                        )}
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorScheme={getTagTypeColor(template.tag_type)}
                        size="sm"
                      >
                        {getTagTypeIcon(template.tag_type)} {template.tag_type}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell color={sc.tableCellMuted} fontSize="sm">
                      {template.unit || "—"}
                    </Table.Cell>
                    <Table.Cell color={sc.tableCellMuted} fontSize="sm">
                      {template.min_threshold ?? "—"}
                    </Table.Cell>
                    <Table.Cell color={sc.tableCellMuted} fontSize="sm">
                      {template.max_threshold ?? "—"}
                    </Table.Cell>
                    <Table.Cell color={sc.tableCellMuted} fontSize="sm">
                      {template.chip_manufacturer ? (
                        <Badge colorScheme="purple" size="sm">
                          {template.chip_manufacturer}
                        </Badge>
                      ) : template.tag_type_match ? (
                        <Badge colorScheme="purple" size="sm">
                          {template.tag_type_match}
                        </Badge>
                      ) : template.cin_brand ? (
                        <Badge colorScheme="purple" size="sm">
                          {template.cin_brand}
                        </Badge>
                      ) : template.nfc_ic_manufacturer ? (
                        <Badge colorScheme="purple" size="sm">
                          {template.nfc_ic_manufacturer}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={1}>
                        <VisionButton
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate({
                              to: "/template-form",
                              search: { templateId: template.id },
                            })
                          }
                        >
                          <FiEdit />
                        </VisionButton>
                        {!template.is_default && (
                          <VisionButton
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTemplate(template)}
                          >
                            <FiTrash2 />
                          </VisionButton>
                        )}
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </VisionCard>
      )}

      <ConfirmDialog
        open={!!templateToDelete}
        onClose={() => setTemplateToDelete(null)}
        onConfirm={() => {
          if (templateToDelete)
            deleteTemplateMutation.mutate(templateToDelete.id)
        }}
        title={t("common:confirm.deleteTitle", {
          type: t("tags:tagTemplates"),
        })}
        message={t("tags:confirmDeleteTemplate", {
          name: templateToDelete?.name,
        })}
        loading={deleteTemplateMutation.isPending}
      />
    </Box>
  )
}
